import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import OpticalSheet from "@/components/OpticalSheet";
import { subjectColor } from "@/lib/constants";
import { ArrowLeft, Save, Eraser, CheckCircle2, Printer, Loader2, ScanLine, FileDown, Upload, BookOpen } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AnswerKey() {
  const { examId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [key, setKey] = useState({});
  const [dirty, setDirty] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selClasses, setSelClasses] = useState([]);
  const [allSchool, setAllSchool] = useState(true);
  const [sheets, setSheets] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [gradeTab, setGradeTab] = useState(""); // "" = varsayılan
  const [mode, setMode] = useState("answers"); // "answers" | "topics"
  const [qTopics, setQTopics] = useState({}); // num -> topic string
  const [dirtyTopics, setDirtyTopics] = useState(false);
  const [curriculum, setCurriculum] = useState({});
  const [examInfo, setExamInfo] = useState(null);
  const [bulk, setBulk] = useState({}); // subject_key -> topic (tümüne ata)
  const [booklet, setBooklet] = useState(""); // Deneme: A/B/C/D (KTT'de boş)
  const [activeQ, setActiveQ] = useState(1); // klavyeyle işaretleme: aktif soru
  const qRefs = useRef({});
  const autoPicked = useRef(false);
  const bookletPicked = useRef(false);
  const keyFileRef = useRef(null);
  const fullFileRef = useRef(null);
  const keyRef = useRef({});            // state'teki anahtarın güncel kopyası
  const ctxRef = useRef({ grade: "", booklet: "" }); // state'teki anahtarın ait olduğu bağlam
  const dirtyRef = useRef(false);
  const reqId = useRef(0);              // yarış önleme: yalnız en son yanıt uygulanır

  const saveTopicsRef = useRef(null);
  const dirtyTopicsRef = useRef(false);

  useEffect(() => { keyRef.current = key; }, [key]);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  useEffect(() => { dirtyTopicsRef.current = dirtyTopics; }, [dirtyTopics]);
  // sayfadan çıkarken bekleyen konu değişikliklerini yaz (debounce beklemeden)
  useEffect(() => () => { if (dirtyTopicsRef.current && saveTopicsRef.current) saveTopicsRef.current(); }, []);

  // Bekleyen değişikliği, DÜZENLENDİĞİ bağlamla (eski kitapçık/sınıf) yazar — kitapçık değişince karışmasın.
  const flushSave = useCallback(async () => {
    if (!dirtyRef.current) return;
    const { grade, booklet: bk } = ctxRef.current;
    const snapshot = keyRef.current;
    try {
      await api.put(`/exams/${examId}/answer-key`, { answer_key: snapshot },
        { params: { ...(grade ? { grade } : {}), ...(bk ? { booklet: bk } : {}) } });
      dirtyRef.current = false; setDirty(false);
    } catch (e) { toast.error(apiError(e)); }
  }, [examId]);

  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    let ignore = false;
    const id = ++reqId.current;
    (async () => {
      await flushSave(); // bağlam değişmeden önce bekleyen kaydı eski bağlamla tamamla
      if (ignore || id !== reqId.current) return;
      const params = { ...(gradeTab ? { grade: gradeTab } : {}), ...(booklet ? { booklet } : {}) };
      const r = await api.get(`/exams/${examId}/answer-key`, { params });
      if (ignore || id !== reqId.current) return; // eski yanıtı yoksay
      setData(r.data); setKey(r.data.answer_key || {});
      ctxRef.current = { grade: gradeTab, booklet };
      setDirty(false); dirtyRef.current = false; setActiveQ(1);
      const tabs = Object.keys(r.data.grade_configs || {}).sort();
      if (!gradeTab && tabs.length > 0 && !autoPicked.current) { autoPicked.current = true; setGradeTab(tabs[0]); }
      if (r.data.exam_type === "deneme" && !booklet && !bookletPicked.current) { bookletPicked.current = true; setBooklet("A"); }
    })();
    return () => { ignore = true; };
  }, [examId, gradeTab, booklet, flushSave]);

  useEffect(() => {
    // sınıf sekmesine göre o sınıfın konularını yükle (izole)
    api.get(`/exams/${examId}/questions`, { params: gradeTab ? { grade: gradeTab } : {} }).then((r) => {
      const m = {};
      r.data.forEach((q) => { if (q.topic) m[String(q.number)] = q.topic; });
      setQTopics(m);
    }).catch(() => {});
  }, [examId, gradeTab]);

  useEffect(() => {
    api.get("/curriculum").then((r) => setCurriculum(r.data)).catch(() => {});
    api.get(`/exams/${examId}`).then((r) => setExamInfo(r.data)).catch(() => {});
  }, [examId]);

  const onKeyFile = async (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const prm = { ...(gradeTab ? { grade: gradeTab } : {}), ...(booklet ? { booklet } : {}) };
      const { data } = await api.post(`/exams/${examId}/answer-key/import`, fd, { params: prm, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Cevap anahtarı yüklendi: ${data.filled}/${data.total} soru${data.errors ? ` (${data.errors} satır atlandı)` : ""}`);
      const r = await api.get(`/exams/${examId}/answer-key`, { params: prm });
      setData(r.data); setKey(r.data.answer_key || {});
    } catch (e) { toast.error(apiError(e, "Cevap anahtarı yüklenemedi")); }
    ev.target.value = "";
  };

  const downloadKeyPdf = async () => {
    try {
      const qs = new URLSearchParams();
      if (gradeTab) qs.set("grade", gradeTab);
      if (booklet) qs.set("booklet", booklet);
      const q = qs.toString();
      await downloadFile(`/exams/${examId}/answer-key.pdf${q ? "?" + q : ""}`, `cevap_anahtari${gradeTab ? "_" + gradeTab + "_sinif" : ""}${booklet ? "_" + booklet : ""}.pdf`);
      toast.success("Cevap anahtarı PDF indirildi");
    } catch (e) { toast.error(apiError(e, "PDF oluşturulamadı")); }
  };

  const downloadAllKeysPdf = async () => {
    try {
      await downloadFile(`/exams/${examId}/answer-keys-all.pdf`, `tum_cevap_anahtarlari.pdf`);
      toast.success("Tüm sınıfların cevap anahtarı indirildi");
    } catch (e) { toast.error(apiError(e, "PDF oluşturulamadı")); }
  };

  const downloadFullTemplate = async () => {
    try {
      await downloadFile(`/exams/${examId}/full-template.xlsx`, "sablon_cevap_konu.xlsx");
      toast.success("Şablon indirildi — tüm sınıf ve dersler için cevap/konu girip geri yükleyin");
    } catch (e) { toast.error(apiError(e, "Şablon oluşturulamadı")); }
  };

  const onFullImport = async (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/exams/${examId}/full-import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Yüklendi: ${data.total_answers} cevap, ${data.total_topics} konu · ${data.sheets.length} sayfa işlendi`);
      const r = await api.get(`/exams/${examId}/answer-key`, { params: { ...(gradeTab ? { grade: gradeTab } : {}), ...(booklet ? { booklet } : {}) } });
      setData(r.data); setKey(r.data.answer_key || {}); setDirty(false); dirtyRef.current = false;
      const rq = await api.get(`/exams/${examId}/questions`, { params: gradeTab ? { grade: gradeTab } : {} });
      const m = {}; rq.data.forEach((q) => { if (q.topic) m[String(q.number)] = q.topic; }); setQTopics(m);
    } catch (e) { toast.error(apiError(e, "Dosya yüklenemedi")); }
    ev.target.value = "";
  };

  const toggleSel = (name) => setSelClasses((s) => s.includes(name) ? s.filter((x) => x !== name) : [...s, name]);

  const generateAndPrint = async () => {
    if (!allSchool && selClasses.length === 0) { toast.error("En az bir sınıf seçin"); return; }
    setGenerating(true);
    try {
      const body = allSchool ? { scope: "school" } : { scope: "multi", classes: selClasses };
      const { data: gen } = await api.post(`/exams/${examId}/optical-forms`, body);
      const { data: forms } = await api.get(`/exams/${examId}/optical-forms`);
      if (!forms.length) { toast.error("Bu kapsamda öğrenci bulunamadı"); return; }
      const sheetData = await Promise.all(forms.map((f) => api.get(`/optical-forms/${f.id}`).then((r) => r.data)));
      setSheets(sheetData);
      setPdfOpen(false);
      setTimeout(() => window.print(), 800);
      toast.success(`${gen.created} yeni form oluşturuldu · toplam ${sheetData.length} optik hazır. Yazdırma penceresinde "PDF olarak kaydet" seçebilirsiniz.`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };

  const downloadPdf = async () => {
    if (!allSchool && selClasses.length === 0) { toast.error("En az bir sınıf seçin"); return; }
    setGenerating(true);
    try {
      const qs = allSchool ? "scope=school" : `classes=${selClasses.join(",")}`;
      await downloadFile(`/exams/${examId}/optical-forms.pdf?${qs}`, "optik_formlar.pdf");
      toast.success("PDF indirildi");
      setPdfOpen(false);
    } catch (e) { toast.error(apiError(e, "PDF oluşturulamadı")); }
    finally { setGenerating(false); }
  };

  const ranges = useMemo(() => {
    if (!data) return [];
    let run = 0;
    return data.subjects.map((s) => { const r = { ...s, start: run + 1, end: run + s.question_count }; run += s.question_count; return r; });
  }, [data]);

  const options = useMemo(() => data?.options || ["A", "B", "C", "D"], [data]);
  const filled = Object.values(key).filter(Boolean).length;

  const setAns = (n, v) => { setKey((k) => { const nk = { ...k }; if (nk[String(n)] === v) delete nk[String(n)]; else nk[String(n)] = v; return nk; }); setDirty(true); };
  const setAnsDirect = (n, v) => { setKey((k) => ({ ...k, [String(n)]: v })); setDirty(true); };
  const clearAns = (n) => { setKey((k) => { const nk = { ...k }; delete nk[String(n)]; return nk; }); setDirty(true); };

  // Klavyeyle işaretleme: A/B/C/D (veya 1-4) ile aktif soruyu işaretle ve ilerle; oklar/Enter ile gez, Backspace/Delete ile sil.
  useEffect(() => {
    if (mode !== "answers") return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable || e.ctrlKey || e.metaKey || e.altKey) return;
      const total = data?.total_questions || 0;
      if (!total) return;
      const letter = (e.key || "").toUpperCase();
      let o = null;
      if (options.includes(letter)) o = letter;
      else if (/^[1-9]$/.test(e.key)) { const idx = parseInt(e.key, 10) - 1; if (idx < options.length) o = options[idx]; }
      if (o) { setAnsDirect(activeQ, o); setActiveQ((q) => Math.min(total, q + 1)); e.preventDefault(); return; }
      if (e.key === "ArrowDown" || e.key === "ArrowRight" || e.key === "Enter") { setActiveQ((q) => Math.min(total, q + 1)); e.preventDefault(); }
      else if (e.key === "ArrowUp" || e.key === "ArrowLeft") { setActiveQ((q) => Math.max(1, q - 1)); e.preventDefault(); }
      else if (e.key === "Backspace") { clearAns(activeQ); setActiveQ((q) => Math.max(1, q - 1)); e.preventDefault(); }
      else if (e.key === " " || e.key === "Delete") { clearAns(activeQ); e.preventDefault(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, activeQ, data, options]);

  // aktif soruyu görünür alana kaydır
  useEffect(() => {
    if (mode !== "answers") return;
    const el = qRefs.current[activeQ];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeQ, mode]);

  const save = useCallback(async () => {
    // Her zaman anahtarın AİT OLDUĞU bağlama (ctxRef) yaz — kitapçık/sınıf değişimi sırasında yanlış kitapçığa yazılmasın.
    const { grade, booklet: bk } = ctxRef.current;
    try {
      await api.put(`/exams/${examId}/answer-key`, { answer_key: keyRef.current },
        { params: { ...(grade ? { grade } : {}), ...(bk ? { booklet: bk } : {}) } });
      toast.success("Cevap anahtarı kaydedildi"); setDirty(false); dirtyRef.current = false;
    }
    catch (e) { toast.error(apiError(e)); }
  }, [examId]);

  // autosave on change (debounced) — DÜZENLENDİĞİ bağlama yazar (flushSave ctxRef kullanır)
  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => { flushSave(); }, 1200);
    return () => clearTimeout(t);
  }, [key, dirty, flushSave]);

  const clearAll = () => { setKey({}); setDirty(true); };

  // ---- Konular (müfredat) ----
  const curGrade = gradeTab || (String(examInfo?.grade || "").match(/\d/)?.[0]) || "8";
  const setTopic = (n, v) => { setQTopics((m) => ({ ...m, [String(n)]: v })); setDirtyTopics(true); };
  const subjectOf = (n) => ranges.find((r) => n >= r.start && n <= r.end)?.key || "";
  const kazanimOf = (subject, topic) => ((curriculum[curGrade] || {})[subject] || []).find((o) => o.t === topic)?.k || "";
  const assignBulk = (skey) => {
    const t = bulk[skey];
    if (!t) { toast.error("Önce konu seçin"); return; }
    const r = ranges.find((x) => x.key === skey);
    if (!r) return;
    setQTopics((m) => {
      const nm = { ...m };
      for (let n = r.start; n <= r.end; n++) nm[String(n)] = t;
      return nm;
    });
    setDirtyTopics(true);
    toast.success(`${r.name}: ${r.start}–${r.end} arası "${t}" olarak atandı`);
  };
  const saveTopics = useCallback(async () => {
    try {
      const topics = {};
      Object.entries(qTopics).forEach(([num, topic]) => {
        if (!topic?.trim()) return;
        const sk = subjectOf(parseInt(num));
        topics[num] = { subject_key: sk, topic: topic.trim(), kazanim: kazanimOf(sk, topic.trim()) };
      });
      await api.put(`/exams/${examId}/question-topics`, { topics }, { params: gradeTab ? { grade: gradeTab } : {} });
      toast.success(`Konular kaydedildi (${gradeTab ? gradeTab + ". sınıf" : "varsayılan"}) · konu bazlı analiz güncellendi`);
      setDirtyTopics(false);
    } catch (e) { toast.error(apiError(e)); }
    // eslint-disable-next-line
  }, [examId, qTopics, ranges, curriculum, curGrade, gradeTab]);
  useEffect(() => {
    if (!dirtyTopics) return;
    const t = setTimeout(saveTopics, 1500);
    return () => clearTimeout(t);
  }, [qTopics, dirtyTopics, saveTopics]);
  useEffect(() => { saveTopicsRef.current = saveTopics; }, [saveTopics]);

  if (!data) return <div className="text-muted-foreground">Yükleniyor...</div>;

  const gradeTabs = Object.keys(data.grade_configs || {}).sort();

  return (
    <div>
      {sheets && (
        <div className="print-area">
          {sheets.map((d) => <OpticalSheet key={d.form.id} data={d} />)}
          <div className="no-print fixed top-4 left-1/2 -translate-x-1/2 z-50 flex gap-2 bg-card border border-border rounded-full px-4 py-2 shadow-lg">
            <Button size="sm" onClick={() => window.print()} data-testid="key-print-btn"><Printer className="h-4 w-4 mr-2" />Yazdır / PDF Kaydet</Button>
            <Button size="sm" variant="outline" onClick={() => setSheets(null)} data-testid="key-print-close">Kapat</Button>
          </div>
        </div>
      )}
      <div className={sheets ? "no-print" : ""}>
      <Button variant="ghost" size="sm" onClick={() => nav("/sinavlar")} className="mb-2 -ml-2"><ArrowLeft className="h-4 w-4 mr-1" />Sınavlar</Button>
      {gradeTabs.length > 0 && (
        <div className="flex gap-1.5 mb-3 flex-wrap" data-testid="grade-tabs">
          <button onClick={() => setGradeTab("")} data-testid="grade-tab-default"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${gradeTab === "" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
            Varsayılan
          </button>
          {gradeTabs.map((g) => (
            <button key={g} onClick={() => setGradeTab(g)} data-testid={`grade-tab-${g}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${gradeTab === g ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
              {g}. Sınıflar
            </button>
          ))}
        </div>
      )}
      {data.exam_type === "deneme" && (
        <div className="flex gap-1.5 mb-3 flex-wrap items-center bg-muted/40 rounded-lg px-3 py-2" data-testid="booklet-tabs">
          <span className="text-xs font-semibold text-foreground mr-1">Kitapçık Türü:</span>
          {(data.booklets || ["A", "B", "C", "D"]).map((b) => (
            <button key={b} onClick={() => setBooklet(b)} data-testid={`booklet-tab-${b}`}
              className={`h-8 w-8 rounded-full text-sm font-bold transition-colors ${booklet === b ? "bg-primary text-primary-foreground" : "bg-background border border-border text-muted-foreground hover:text-foreground"}`}>
              {b}
            </button>
          ))}
          <span className="text-[11px] text-muted-foreground ml-2">Her kitapçığın cevap anahtarı ayrı kaydedilir — seçili kitapçık: <b>{booklet || "A"}</b></span>
        </div>
      )}
      <div className="flex gap-1.5 mb-4" data-testid="key-mode-tabs">
        <button onClick={() => setMode("answers")} data-testid="mode-answers"
          className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${mode === "answers" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
          Cevaplar
        </button>
        <button onClick={() => setMode("topics")} data-testid="mode-topics"
          className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors flex items-center gap-1.5 ${mode === "topics" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
          <BookOpen className="h-3.5 w-3.5" />Konular (2026 Müfredat)
        </button>
      </div>
      <PageHeader title="Cevap Anahtarı" desc={`${filled} / ${data.total_questions} soru işaretlendi · değişiklikler otomatik kaydedilir`}>
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="outline" data-testid="key-clear"><Eraser className="h-4 w-4 mr-2" />Tümünü Temizle</Button></AlertDialogTrigger>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader><AlertDialogTitle>Tüm cevaplar silinsin mi?</AlertDialogTitle>
              <AlertDialogDescription>Bu işlem girilen tüm cevap anahtarını temizler. Yanlışlıkla yapıldıysa vazgeçin.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Vazgeç</AlertDialogCancel><AlertDialogAction onClick={clearAll} className="bg-destructive text-destructive-foreground">Temizle</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <input type="file" accept=".xlsx,.csv,text/csv" ref={keyFileRef} onChange={onKeyFile} className="hidden" data-testid="key-import-file" />
        <input type="file" accept=".xlsx" ref={fullFileRef} onChange={onFullImport} className="hidden" data-testid="full-import-file" />
        <Button variant="outline" onClick={downloadFullTemplate} data-testid="full-template"><FileDown className="h-4 w-4 mr-2" />Tam Şablon (Excel)</Button>
        <Button variant="outline" onClick={() => fullFileRef.current?.click()} data-testid="full-import"><Upload className="h-4 w-4 mr-2" />Tam Excel Yükle</Button>
        <Button variant="outline" onClick={() => keyFileRef.current?.click()} data-testid="key-import"><Upload className="h-4 w-4 mr-2" />Excel Yükle</Button>
        <Button variant="outline" onClick={downloadKeyPdf} data-testid="key-pdf"><FileDown className="h-4 w-4 mr-2" />Anahtar PDF</Button>
        <Button variant="outline" onClick={downloadAllKeysPdf} data-testid="key-pdf-all"><FileDown className="h-4 w-4 mr-2" />Tüm Sınıflar (Tek PDF)</Button>
        <Button variant="outline" onClick={() => setPdfOpen(true)} data-testid="key-optic-pdf"><ScanLine className="h-4 w-4 mr-2" />Optik Form PDF</Button>
        <Button onClick={mode === "topics" ? saveTopics : save} data-testid="key-save"><Save className="h-4 w-4 mr-2" />Kaydet</Button>
      </PageHeader>

      <p className="text-xs text-muted-foreground -mt-2 mb-4"><b>Tam Şablon (Excel):</b> tüm sınıf ve dersler için cevap + konu girişi yapabileceğiniz tek dosyayı indirir; doldurup <b>Tam Excel Yükle</b> ile geri yükleyin. Alternatif olarak sadece cevap için <b>Excel Yükle</b> formatı: her satır <code className="font-mono">soru_no;cevap</code> (örn. <code className="font-mono">1;A</code>).</p>

      {filled >= data.total_questions && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 rounded-lg px-4 py-2.5 mb-4">
          <CheckCircle2 className="h-4 w-4" /> Cevap anahtarı tamamlandı. Sınav sonuçlandırmaya hazır.
        </div>
      )}

      {mode === "topics" ? (
        <div>
          <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/40 rounded-lg px-4 py-2.5 mb-4">
            <BookOpen className="h-4 w-4 mt-0.5 text-primary shrink-0" />
            <p>Her soruya güncel <b>2026 Maarif müfredatından</b> konu atayın — şu an <b>{gradeTab ? gradeTab + ". sınıflar" : "varsayılan"}</b> sekmesindesiniz ve atamalar <b>sadece bu sınıf seviyesine</b> kaydedilir; diğer sınıflar etkilenmez. Bir dersin tüm soruları aynı konudan ise üstten seçip <b>"Tümüne Ata"</b> deyin; farklıysa satır satır seçin ya da elle yazın. Konu bazlı analiz otomatik oluşur.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ranges.map((s) => {
              const c = subjectColor(s.key);
              const curTopics = (curriculum[curGrade] || {})[s.key] || [];
              const dlId = `dl-${s.key}-${curGrade}`;
              return (
                <Card key={s.key} className="p-4 bg-card border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${c.badge}`}>{s.name}</span>
                    <span className="text-xs text-muted-foreground font-mono">{s.start}–{s.end}</span>
                  </div>
                  <div className="flex gap-1.5 mb-3">
                    <Select value={bulk[s.key] || ""} onValueChange={(v) => setBulk((b) => ({ ...b, [s.key]: v }))}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`topic-bulk-${s.key}`}><SelectValue placeholder="Konu seç…" /></SelectTrigger>
                      <SelectContent className="bg-card border-border max-h-64">
                        {curTopics.map((o) => <SelectItem key={o.t} value={o.t}><span className="font-mono text-[10px] text-muted-foreground mr-1.5">{o.k}</span>{o.t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" className="h-8 text-xs shrink-0" onClick={() => assignBulk(s.key)} data-testid={`topic-assign-${s.key}`}>Tümüne Ata</Button>
                  </div>
                  <datalist id={dlId}>{curTopics.map((o) => <option key={o.t} value={o.t} />)}</datalist>
                  <div className="space-y-1.5">
                    {Array.from({ length: s.question_count }, (_, i) => s.start + i).map((n) => (
                      <div key={n} className="flex items-center gap-2">
                        <span className="w-7 text-right font-mono text-xs font-semibold text-muted-foreground">{n}</span>
                        <input list={dlId} value={qTopics[String(n)] || ""} onChange={(e) => setTopic(n, e.target.value)}
                          placeholder="Konu seç / yaz" data-testid={`topic-${n}`}
                          className="flex h-7 w-full rounded-md border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary" />
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      ) : (
      <>
      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-4 py-2.5 mb-4" data-testid="kbd-help">
        <span>⌨️ <b>Klavyeyle hızlı giriş:</b> bir soruya tıklayıp seçin; <b>A/B/C/D</b> (veya <b>1–4</b>) tuşuna basınca işaretlenir ve otomatik alt soruya geçer. <b>↑ ↓ ← →</b> veya <b>Enter</b> ile gezin, <b>Backspace</b> ile silin.</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ranges.map((s) => {
          const c = subjectColor(s.key);
          return (
            <Card key={s.key} className="p-4 bg-card border-border">
              <div className="flex items-center justify-between mb-3">
                <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${c.badge}`}>{s.name}</span>
                <span className="text-xs text-muted-foreground font-mono">{s.start}–{s.end}</span>
              </div>
              <div className="space-y-1.5">
                {Array.from({ length: s.question_count }, (_, i) => s.start + i).map((n) => {
                  const isActive = n === activeQ;
                  return (
                  <div key={n} ref={(el) => { qRefs.current[n] = el; }} onClick={() => setActiveQ(n)}
                    data-testid={`answer-row-${n}`}
                    className={`flex items-center gap-2 rounded-md px-1 py-0.5 cursor-pointer transition-colors ${isActive ? "ring-2 ring-primary bg-primary/5" : ""}`}>
                    <span className={`w-7 text-right font-mono text-xs font-semibold ${isActive ? "text-primary" : "text-muted-foreground"}`}>{n}</span>
                    <div className="flex gap-1.5">
                      {options.map((o) => {
                        const active = key[String(n)] === o;
                        return (
                          <button key={o} onClick={(e) => { e.stopPropagation(); setActiveQ(n); setAns(n, o); }} data-testid={`answer-${n}-${o}`}
                            className={`h-7 w-7 rounded-full border text-xs font-semibold transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/60 text-foreground/70"}`}>
                            {o}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>
      </>
      )}

      <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader><DialogTitle>Optik Form PDF Oluştur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Bu sınav için optik formlar oluşturulup baskı önizlemesi açılacak. Yazdırma penceresinde <b>"PDF olarak kaydet"</b> seçebilirsiniz.</p>
            <label className="flex items-center gap-2 p-2 rounded-md hover:bg-muted cursor-pointer" data-testid="pdf-scope-school">
              <Checkbox checked={allSchool} onCheckedChange={(v) => setAllSchool(!!v)} />
              <span className="text-sm font-medium">Tüm Okul</span>
            </label>
            {!allSchool && (
              <div className="space-y-1.5 max-h-48 overflow-y-auto ktt-scroll border border-border rounded-md p-1">
                {classes.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted cursor-pointer" data-testid={`pdf-class-${c.name}`}>
                    <Checkbox checked={selClasses.includes(c.name)} onCheckedChange={() => toggleSel(c.name)} />
                    <span className="text-sm">{c.name}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{c.student_count} öğrenci</span>
                  </label>
                ))}
                {classes.length === 0 && <p className="text-xs text-muted-foreground p-2">Önce sınıf/öğrenci ekleyin.</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfOpen(false)}>Vazgeç</Button>
            <Button variant="outline" onClick={downloadPdf} disabled={generating} data-testid="pdf-download-btn">
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}PDF İndir
            </Button>
            <Button onClick={generateAndPrint} disabled={generating} data-testid="pdf-generate-btn">
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}Önizleme & Yazdır
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
